import { Controller } from "@hotwired/stimulus"

// The glyph palette behind every comment's react button. The controller sits on the single panel
// (see layouts/application) rather than on each trigger, and listens on the document — the same
// singleton the snooze menu uses, and for the same reason: a twenty-note thread would put twenty
// hidden palettes of fifty-six buttons each in the DOM for the one that is ever open.
//
// It cannot reuse dropdown_controller, for the reason snooze_popover_controller documents: that one
// assumes the menu is a descendant of its own element. This panel is parented to the body and the
// form's action is rewritten from the invoking button's comment on each open.
//
// Nor can it reuse the composer's emoji palette, which shares the same server-rendered glyph list:
// that one inserts text into a contenteditable at a saved caret, and this one submits a form. The
// list is what they share, and it is shared in ComposerEmoji.
const VIEWPORT_MARGIN = 8
const TRIGGER_GAP = 6
const TRIGGER_SELECTOR = "[data-reaction-palette]"

export default class extends Controller {
  static targets = ["form", "grid"]

  connect() {
    this.openFromEvent = this.openFromEvent.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.closeOnScroll = this.closeOnScroll.bind(this)
    this.closeOnSubmit = this.closeOnSubmit.bind(this)

    document.addEventListener("click", this.openFromEvent)
    document.addEventListener("keydown", this.closeOnEscape)
    // Capturing, because the container that would shear this panel is .thread__scroll — a scroll
    // event does not bubble out of it to the document. Without this a fixed panel stays where it
    // was painted while the note it belongs to slides away.
    document.addEventListener("scroll", this.closeOnScroll, true)
    this.formTarget.addEventListener("submit", this.closeOnSubmit)
  }

  disconnect() {
    this.close()
    document.removeEventListener("click", this.openFromEvent)
    document.removeEventListener("keydown", this.closeOnEscape)
    document.removeEventListener("scroll", this.closeOnScroll, true)
    this.formTarget.removeEventListener("submit", this.closeOnSubmit)
  }

  openFromEvent(event) {
    const trigger = event.target.closest?.(TRIGGER_SELECTOR)
    if (trigger) {
      this.toggle(trigger)
      return
    }

    if (!this.element.hidden && !this.element.contains(event.target)) this.close()
  }

  toggle(trigger) {
    if (this.trigger === trigger && !this.element.hidden) {
      this.close()
      return
    }

    this.open(trigger)
  }

  open(trigger) {
    this.trigger = trigger
    this.formTarget.action = trigger.dataset.reactionUrl
    this.element.hidden = false
    this.position(trigger)
    this.markExpanded(true)
    // Focusing a glyph can make the browser scroll a container into view, and the capturing scroll
    // listener would otherwise close the palette on the same click that opened it.
    this.ignoreScroll = true
    this.focusOption(0)
    requestAnimationFrame(() => { this.ignoreScroll = false })
  }

  close() {
    if (this.element.hidden) return

    this.element.hidden = true
    this.markExpanded(false)
    this.trigger = null
  }

  closeOnEscape(event) {
    if (event.key !== "Escape" || this.element.hidden) return

    const trigger = this.trigger
    this.close()
    trigger?.focus()
  }

  closeOnScroll() {
    if (this.ignoreScroll) return

    this.close()
  }

  // Closed as the reaction goes out rather than when it comes back: the panel is over the card whose
  // chips are about to change, and leaving it up would hide the answer to the click.
  closeOnSubmit() {
    this.close()
  }

  // Below the trigger by default, flipped above when there is no room. The react button sits on the
  // trailing edge of a note's meta row, so the panel is aligned to its right edge — a left-aligned
  // one would hang off the card into the info rail.
  position(trigger) {
    const anchor = trigger.getBoundingClientRect()
    const panel = this.element.getBoundingClientRect()

    const below = anchor.bottom + TRIGGER_GAP
    const top = below + panel.height > window.innerHeight - VIEWPORT_MARGIN
      ? anchor.top - panel.height - TRIGGER_GAP
      : below

    const rightAligned = anchor.right - panel.width
    const rightmost = window.innerWidth - panel.width - VIEWPORT_MARGIN
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rightAligned, rightmost))

    this.element.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`
    this.element.style.left = `${left}px`
  }

  markExpanded(open) {
    document.querySelectorAll(`${TRIGGER_SELECTOR}[aria-expanded]`).forEach((button) => {
      button.setAttribute("aria-expanded", button === this.trigger && open ? "true" : "false")
    })
  }

  moveFocus(event) {
    const index = this.nextIndex(event)
    if (index === null) return

    event.preventDefault()
    this.focusOption(index)
  }

  // Null for a key the palette has no answer for, so the caller leaves it to the browser — Enter and
  // Space must reach the button and submit it. Wrapped rather than clamped, as the composer's own
  // palette is: the grid is a ring, and a press that does nothing reads as a dead key.
  nextIndex(event) {
    const options = this.options
    if (options.length === 0) return null
    if (event.key === "Home") return 0
    if (event.key === "End") return options.length - 1

    const columns = this.columns(options)
    const steps = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns }
    if (!(event.key in steps)) return null

    const current = options.indexOf(event.currentTarget)
    return (current + steps[event.key] + options.length) % options.length
  }

  // Measured off the layout rather than restated here: the grid is auto-filled, so how many glyphs
  // sit on a row is the stylesheet's answer.
  columns(options) {
    const firstRow = options[0].offsetTop

    return options.filter((option) => option.offsetTop === firstRow).length || 1
  }

  // One tab stop for the whole palette, moved to whichever glyph is current — fifty-six stops would
  // be worse than no keyboard support at all.
  focusOption(index) {
    const options = this.options
    const target = options[index]
    if (!target) return

    options.forEach((option) => option.setAttribute("tabindex", "-1"))
    target.setAttribute("tabindex", "0")
    target.focus()
  }

  get options() {
    return Array.from(this.gridTarget.querySelectorAll(".reaction-palette__option"))
  }
}
